/**
 * Controlador principal para el módulo de archivos de participantes.
 *
 * Este controlador maneja toda la lógica de negocio para la gestión de archivos
 * de carga masiva de participantes, incluyendo listado, filtrado, descarga y
 * visualización de reportes. Proporciona una interfaz completa para administrar
 * los archivos Excel cargados para importar participantes.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-12]
 * @updated [Fecha de última modificación: 2025-08-12]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import Spinner from "components/MDSpinner";
import MDAlert from "components/Alert";
import FilterController from "components/filter/controller/FilterController";
import ParticipantFilesList from "../modules/ParticipantFilesList";
import { ParticipantFilesReport } from "../modules/ParticipantFilesReport";
import { downloadFile } from "utils/fileUtils";
import { ParticipantFileDownloadService } from "../services/ParticipantFilesService";

export default function ParticipantFilesController(activityName) {
  const { id: activityId } = useParams();
  const filterControllerRef = useRef();

  // Configuración del filtro para archivos de participantes
  const filterConfig = [
    { visibleName: "Nombre del archivo", dbField: "original_filename", type: "text" },
    { visibleName: "Estado", dbField: "status", type: "select", source: "statusTypes" },
    { visibleName: "Fecha de carga", dbField: "created", type: "date" },
    { visibleName: "Fecha de procesamiento", dbField: "processed_at", type: "date" },
    { visibleName: "Total de filas", dbField: "total_rows", type: "text" },
    { visibleName: "Filas exitosas", dbField: "successful_rows", type: "text" },
    { visibleName: "Filas con errores", dbField: "error_rows", type: "text" },
    { visibleName: "Responsable", dbField: "user_email", type: "text" },
  ];

  const dataTable = "participant_uploads";

  // Variables de paginación
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesStart, setEntriesStart] = useState(0);
  const [entriesEnd, setEntriesEnd] = useState(0);

  // Variables de listado
  const [participantFilesList, setParticipantFilesList] = useState([]);

  // Variables de reporte
  const [openReport, setOpenReport] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);

  // Controladores comunes
  const [loading, setLoading] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [isAlert, setIsAlert] = useState(false);

  /**
   * Maneja las alertas del sistema
   * @param {string} typeAlert - Tipo de alerta (success, fail, etc.)
   * @param {string} message - Mensaje de la alerta
   */
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  /**
   * Maneja el cambio de página en la paginación
   * @param {Event} event - Evento de paginación
   * @param {number} value - Número de página seleccionada
   */
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  /**
   * Ejecuta la búsqueda/filtrado del controlador de filtros
   */
  const callHandleClickSubmit = () => {
    if (filterControllerRef.current) {
      filterControllerRef.current.handleClickSubmit();
    }
  };

  /**
   * Maneja la apertura del modal de reporte
   * @param {Object} upload - Datos del archivo de carga
   */
  const handleViewReport = (upload) => {
    setSelectedUpload(upload);
    setOpenReport(true);
  };

  /**
   * Cierra el modal de reporte
   */
  const closeReport = () => {
    setOpenReport(false);
    setSelectedUpload(null);
  };

  /**
   * Maneja la descarga de archivos
   * @param {string} uploadId - ID del archivo de carga
   */
  const handleDownloadFile = async (uploadId) => {
    try {
      setLoading(true);
      const response = await ParticipantFileDownloadService(uploadId);

      if (response.status) {
        console.log(response.contentDisposition);
        downloadFile(
          response.file,
          "Archivo_participantes.xls",
          response.contentDisposition,
          (message) => handleAlert("success", message),
          (error) => handleAlert("fail", error),
          "Archivo descargado exitosamente",
          "Error al descargar el archivo"
        );
      } else {
        handleAlert("fail", "Error al descargar el archivo");
      }
    } catch (error) {
      handleAlert("fail", "Error al descargar el archivo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Spinner loading={loading} />
      <MDAlert isAlert={isAlert} setIsAlert={setIsAlert} typeAlert={typeAlert} message={message} />
      <FilterController
        filterConfig={filterConfig}
        dataTable={dataTable}
        ref={filterControllerRef}
        setTotalData={setTotalData}
        setEntriesEnd={setEntriesEnd}
        setEntriesStart={setEntriesStart}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
        setListData={setParticipantFilesList}
        handleAlert={handleAlert}
        activity_id={activityId}
      />
      <ParticipantFilesList
        participantFilesList={participantFilesList}
        entriesStart={entriesStart}
        entriesEnd={entriesEnd}
        totalData={totalData}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handleViewReport={handleViewReport}
        handleDownloadFile={handleDownloadFile}
        callHandleClickSubmit={callHandleClickSubmit}
        activityName={activityName}
      />
      <ParticipantFilesReport
        open={openReport}
        handleClose={closeReport}
        uploadData={selectedUpload}
      />
    </>
  );
}
